import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { isCalendarPath, portalAppTitleForPath } from '../lib/portal';

const DEFAULT_ICONS = [
  { rel: 'icon', href: '/favicon-32.png', type: 'image/png', sizes: '32x32' },
  { rel: 'icon', href: '/favicon-16.png', type: 'image/png', sizes: '16x16' },
  { rel: 'apple-touch-icon', href: '/apple-touch-icon.png', sizes: '180x180' },
] as const;

const CALENDAR_ICONS = [
  { rel: 'icon', href: '/calendar-favicon-32.png', type: 'image/png', sizes: '32x32' },
  { rel: 'icon', href: '/calendar-favicon-16.png', type: 'image/png', sizes: '16x16' },
  { rel: 'apple-touch-icon', href: '/calendar-icon.png', sizes: '180x180' },
] as const;

const DEFAULT_MANIFEST = '/site.webmanifest';
const CALENDAR_MANIFEST = '/calendar.webmanifest';

function ensureHeadLink(attrs: {
  rel: string;
  href: string;
  type?: string;
  sizes?: string;
}): HTMLLinkElement {
  const selector = attrs.sizes
    ? `link[rel="${attrs.rel}"][sizes="${attrs.sizes}"]`
    : `link[data-bth-favicon="${attrs.rel}"]`;
  let el = document.head.querySelector<HTMLLinkElement>(selector);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('data-bth-favicon', attrs.rel);
    document.head.appendChild(el);
  }
  el.rel = attrs.rel;
  el.href = attrs.href;
  if (attrs.type) el.type = attrs.type;
  else el.removeAttribute('type');
  if (attrs.sizes) el.setAttribute('sizes', attrs.sizes);
  else el.removeAttribute('sizes');
  return el;
}

function applyFavicons(calendar: boolean) {
  document.head
    .querySelectorAll('link[data-bth-favicon], link[rel="icon"], link[rel="apple-touch-icon"]')
    .forEach((node) => node.parentElement?.removeChild(node));

  const icons = calendar ? CALENDAR_ICONS : DEFAULT_ICONS;
  for (const icon of icons) {
    ensureHeadLink(icon);
  }
}

function applyManifest(calendar: boolean) {
  let link = document.head.querySelector<HTMLLinkElement>('link[rel="manifest"]');
  if (!link) {
    link = document.createElement('link');
    link.rel = 'manifest';
    document.head.appendChild(link);
  }
  // Cache-bust so the browser re-reads icons when switching portals.
  const href = calendar ? CALENDAR_MANIFEST : DEFAULT_MANIFEST;
  link.href = `${href}?v=${calendar ? 'cal' : 'portal'}`;
}

function applyAppMeta(calendar: boolean) {
  const title = calendar ? 'BTH Calendar' : 'BTH Portal';
  for (const name of ['application-name', 'apple-mobile-web-app-title'] as const) {
    let meta = document.head.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = name;
      document.head.appendChild(meta);
    }
    meta.content = title;
  }
}

/** Keeps tab title, favicon, and PWA manifest in sync with the route. */
export function DocumentTitle() {
  const { pathname } = useLocation();

  useEffect(() => {
    const calendar = isCalendarPath(pathname);
    document.title = portalAppTitleForPath(pathname);
    applyFavicons(calendar);
    applyManifest(calendar);
    applyAppMeta(calendar);
  }, [pathname]);

  return null;
}
